import { Box, Button, styled } from "@mui/material"
import { hasRole } from "auth/hasRole"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import ClpTable from "components/ClpTable"
import ContinueDialog from "components/Dialogs/ContinueDialog"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import RoleTemplateForm from "./RoleTemplateForm"
import { IRole, IRoleTemplate } from "./roleTemplates.interface"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"

const StyledBox = styled(Box)(({ theme }) => ({
  background: (theme.palette as any).foreground.inverted,

  width: "100%",
  [theme.breakpoints.up("sm")]: {
    "& > div": {
      position: "relative",
      "& .MuiTableContainer-root": {
        overflow: "auto"
      }
    }
  }
}))

const RoleTemplatesList = (props: { isSystemAdmin: boolean; customerId: number }) => {
  const appContext = useContext(AppContext)
  const { t } = useTranslation()
  const [roleTemplatesResult, roleTemplatesApi] = useApi() as [IApiResult, IApiFunc]
  const [roleTemplateDetailsResult, roleTemplateDetailsApi] = useApi() as [IApiResult, IApiFunc]
  const [roleTemplateResult, roleTemplateApi] = useApi() as [IApiResult, IApiFunc]
  const [deleteRoleTemplateResult, deleteRoleTemplateApi] = useApi() as [IApiResult, IApiFunc]
  const [initialValues, setInitialValues] = useState<IRoleTemplate>({
    id: 0,
    roles: [],
    name: "",
    global: false,
    customerId: props.customerId
  })
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    selectedId: number
    name: string
  }>({ open: false, selectedId: 0, name: "" })

  const handleEdit = (id: number) => {
    roleTemplateDetailsApi.get(apiEndpoints.customeradmin.gettemplaterole.replace("{id}", id.toString()))
  }

  const handleSubmit = (objectToSave: any) => {
    roleTemplateApi.post(apiEndpoints.customeradmin.createorupdateuserrolestemplates, objectToSave)
  }

  useEffect(() => {
    if (roleTemplateDetailsResult.status === 2) {
      setInitialValues(roleTemplateDetailsResult.data)

      !appContext.isSidebarOpen && appContext.toggleSideBar(true)
    }
  }, [roleTemplateDetailsResult])

  useEffect(() => {
    if (!appContext.isSidebarOpen) {
      setInitialValues({
        id: 0,
        roles: [],
        name: "",
        global: false,
        customerId: props.customerId
      })
    }
  }, [appContext.isSidebarOpen])

  useEffect(() => {
    roleTemplatesApi.get(
      apiEndpoints.customeradmin.getuserrolestemplates.replace("{customerId}", props.customerId.toString())
    )
  }, [roleTemplateResult, deleteRoleTemplateResult])

  useEffect(() => {
    if (roleTemplateResult.status === 2) appContext.toggleSideBar()
  }, [roleTemplateResult])

  useEffect(() => {
    if (deleteRoleTemplateResult.status === 2) setDeleteDialog({ open: false, selectedId: 0, name: "" })
  }, [deleteRoleTemplateResult])

  const handleDelete = () => {
    deleteRoleTemplateApi.del(
      apiEndpoints.customeradmin.deleteroletemplate.replace("{id}", deleteDialog.selectedId.toString())
    )
  }
  const openDeleteDialog = (id: number, name: string) => {
    setDeleteDialog({ open: true, selectedId: id, name: name })
  }

  const columns: any = [
    {
      accessorKey: "name",
      header: t("settings.form.name", "Name")
    },
    {
      accessorKey: "roles",
      header: t("settings.form.roletemplate.connected_roles", "Connected roles"),
      cell: ({ row }: any) => {
        return (
          row?.original?.roles
            .map((x: IRole) => t("settings.user.modules." + x.translateId, x.translateId))
            .join(", ") ?? ""
        )
      }
    },
    {
      accessorKey: "global",
      header: t("settings.form.global", "Global"),
      cell: ({ row }: any) => {
        return row.original.global ? t("settings.form.yes", "Yes") : t("settings.form.no", "No")
      }
    },
    {
      accessorKey: "createdBy",
      header: t("settings.form.created_by", "Created by"),
      cell: ({ row }: any) => {
        return row.original.createdBy
      }
    },
    {
      accessorKey: "edit",
      header: "",
      meta: {
        align: "right"
      },
      cell: ({ cell }: any) => {
        return hasRole(appContext.appUser, allRoles.CustomerAdmin) ||
          cell.row.original.createdById === appContext.appUser.profile.id ? (
          <StyledTableButtonGroup>
            <TableButton
              type={TableButtonTypeEnum.Edit}
              handleOnClick={() => {
                handleEdit(cell.row.original.id)
              }}></TableButton>
            <TableButton
              type={TableButtonTypeEnum.Delete}
              handleOnClick={() => openDeleteDialog(cell.row.original.id, cell.row.original.name)}
            />
          </StyledTableButtonGroup>
        ) : (
          <></>
        )
      }
    }
  ]

  return (
    <>
      <RoleTemplateForm values={initialValues} handleSubmit={handleSubmit} />

      <ContinueDialog
        onAddSubmit={handleDelete}
        message={
          t("settings.form.roletemplate.confirm_delete", "Do you want to remove role template") +
          ` ${deleteDialog.name}?`
        }
        continueBtnText={t("settings.form.button.remove_roletemplate", "Remove roletemplate")}
        cancelBtnText={t("global.buttons.cancel", "Cancel")}
        result={deleteRoleTemplateResult}
        setShowContinueModal={(value: any) => setDeleteDialog({ open: value, selectedId: 0, name: "" })}
        isOpen={deleteDialog.open}
      />

      <Box px={{ xs: 2, sm: 3 }} pt={{ xs: 2, sm: 3 }}>
        <Box display="flex" flexDirection="row" justifyContent="flex-end">
          <Box order={3}>
            <Button onClick={() => appContext.toggleSideBar()} variant="contained" color="primary">
              {t("settings.form.button.create_roletemplate", "Create role template")}
            </Button>
          </Box>
        </Box>
      </Box>
      {roleTemplatesResult.status === 2 && roleTemplatesResult.data && (
        <ClpTable
          rows={roleTemplatesResult.data}
          columns={columns}
          loadingState={roleTemplatesResult.status}
        />
      )}

    </>
  )
}

export default RoleTemplatesList
